import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse, HttpContextToken } from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthenticationService } from '../_services/authentication.service';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { IndexedDBService } from '../_services/indexdDb.service';
import { environment } from '../../environments/environment';

// marca a requisição já repetida após renovar o token CSRF, para não entrar em loop
const CSRF_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authenticationService: AuthenticationService,
    public translate: TranslateService,
    private indexedDbService: IndexedDBService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // console.log('intercepted request ... ');
    // console.log(request.url);
    
    const userLogged = this.authenticationService.isLoggedInValue();
    
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        // console.log(err);
        
        // Se a URL contém 'callReturnUrl', apenas propaga o erro
        if (err.url && err.url.indexOf('callReturnUrl') !== -1) {
          return throwError(() => err);
        }

        // Token CSRF vencido/sobrescrito (ex.: dev e prod dividem o cookie XSRF-TOKEN em .docsia.com.br):
        // renova o token e repete a requisição uma vez, em vez de limpar a sessão e recarregar a página
        if (err.status === 403 && err.error?.error === 'CSRF_TOKEN_INVALID' && !request.context.get(CSRF_RETRIED)) {
          return this.retryWithNewCsrfToken(request, next);
        }

        // Login com KTIdentity: erros com código ({ message, code }) vão para a tela de login,
        // sem o tratamento global (que suprime erros de quem não está logado)
        if (request.url.indexOf(`${environment.baseUrl}/auth/`) === 0 && typeof err.error?.code === 'string') {
          return throwError(() => err);
        }

        // Obtém o status de erro atual
        const statusErr = this.authenticationService.currenStatusErrValue;
        this.authenticationService.currentStatusErrSubject.next(err.status);

        // Processa diferentes tipos de erro
        return this.handleErrorStatus(err, statusErr, userLogged);
      })
    );
  }

  private retryWithNewCsrfToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // next.handle em vez de HttpClient: injetar HttpClient num interceptor gera dependência circular
    const csrfRequest = new HttpRequest('GET', environment.baseUrl.replace('/api', '/csrf'), { withCredentials: true });
    return next.handle(csrfRequest).pipe(
      filter((event): event is HttpResponse<any> => event instanceof HttpResponse),
      take(1),
      switchMap(() => this.intercept(request.clone({
        // o JwtInterceptor já rodou com o token antigo; relê o cookie que o /csrf acabou de gravar
        headers: request.headers.set('X-XSRF-TOKEN', this.getCookie('XSRF-TOKEN')),
        context: request.context.set(CSRF_RETRIED, true)
      }), next))
    );
  }

  private getCookie(name: string): string {
    const cookie = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(`${name}=`));
    return cookie ? cookie.substring(name.length + 1) : '';
  }

  private handleErrorStatus(
    err: HttpErrorResponse, 
    statusErr: number, 
    userLogged: boolean
  ): Observable<never> {
    
    switch(err.status) {
      case 405:
        return this.handle405Error(statusErr);
        
      case 500:
        return this.handle500Error(statusErr, userLogged);
        
      case 401:
        return this.handle401Error(err);
      
      case 403:
        return this.handle403Error(statusErr);
        
      default:
        return this.handleDefaultError(err, userLogged);
    }
  }

  private handle403Error(statusErr: number): Observable<never>{
    this.indexedDbService.clearStore('userData').then(result =>{console.log(result)})
    localStorage.clear();
    sessionStorage.clear();
    location.reload();

    return EMPTY;
  }

  private handle405Error(statusErr: number): Observable<never> {
    if(statusErr === 0) {
      Swal.fire({
        title: this.translate.instant('INTERCEPTOR.withoutAcess'),
        html: this.translate.instant('INTERCEPTOR.thereIsNoAcessProfile'),
        showCloseButton: true
      }).then((result) => {
        // Usar arrow function para manter o contexto do 'this'
        this.authenticationService.logout().subscribe((data: any) => {
          this.authenticationService.currentStatusErrSubject.next(0);
          // console.log(data);
          location.reload();
        });
      });
    }
    
    // Retorna EMPTY para suprimir o erro após o tratamento
    return EMPTY;
  }

  private handle500Error(statusErr: number, userLogged: boolean): Observable<never> {
    if(statusErr === 0 && userLogged) {
      const token = localStorage.getItem("token");
      if (token=='null' || !token){
        this.indexedDbService.clearStore('userData').then(result =>{console.log(result)})
        localStorage.clear();
        sessionStorage.clear();
        location.reload();
      }else{

        Swal.fire({
          title: 'Oops',
          html: this.translate.instant('INTERCEPTOR.anUnexpectedErrorOcurred'),
          showCloseButton: true
        }).then((result) => {
          this.authenticationService.currentStatusErrSubject.next(0);
          // console.log(err);
        });
        
        // Retorna EMPTY para suprimir o erro após o tratamento
      }
      return EMPTY;
    }
    
    // Se não tratou o erro, propaga
    return throwError(() => new Error('Server error'));
  }

  private handle401Error(err: HttpErrorResponse): Observable<never> {
    this.authenticationService.currentStatusErrSubject.next(0);
    
    // Verifica mensagens específicas de erro 401
    const errorMessage = err.error?.message;
    
    if (errorMessage === this.translate.instant('INTERCEPTOR.thisUserIsLoggedInAnotherDevice') ||
        errorMessage === this.translate.instant('INTERCEPTOR.userNoRegisteredInSystem')) {
      return throwError(() => err);
    }
    
    // Para outros casos de 401, limpa o usuário atual
    this.authenticationService.currentUserSubject.next(null);
    // Retorna EMPTY para suprimir o erro
    return EMPTY;
  }

  private handleDefaultError(err: HttpErrorResponse, userLogged: boolean): Observable<never> {
    if(userLogged) {
      this.authenticationService.currentStatusErrSubject.next(0);
      return throwError(() => err);
    }
    
    // Para usuários não logados, suprime erros por padrão
    return EMPTY;
  }
}
